import { useQueryClient } from "@tanstack/react-query";
import { Factory, Globe, Loader2, Mail, UsersRound } from "lucide-react";
import { FormEvent, useContext, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useSearchParams } from "react-router-dom";
import { OrganizationType } from "~/api/model";
import { useCreateOrganization } from "~/api/organizations/organizations";
import { getGetCurrentUserQueryKey } from "~/api/users/users";
import { LocalizationContext } from "~/components/LocalizationWrapper";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { SKY_ACCOUNT_CREATED } from "~/constants/index";
import {
  isCustomEmailDomain,
  removeAllTokens,
  ROUTES,
  validateEmail,
} from "~/helpers";
import { useThemedOnboardingImage } from "~/hooks/useThemedOnboardingImage";
import { useTrackAccountCreation } from "~/hooks/useTrackAccountCreation";
import { trackEvent } from "~/utils/analytics";
import { initiateOAuthLogin, SocialProvider } from "~/utils/auth/socialAuth";
import logger from "~/utils/logger";
import { useAuthStepImage } from "../hooks/useAuthStepImage";
import { LocationSelector } from "./LocationSelector";
import { RegisterDemoWidget } from "./RegisterDemoWidget";
import { StepIndicator } from "./StepIndicator";
import { TermsAndPolicyDialog } from "./TermsAndPolicyDialog";

enum REGISTER_STEP {
  PERSONAL_DETAILS = 1,
  COMPANY_DETAILS_NAME = 2,
  LOCATION = 3,
  COMPANY_DETAILS_TYPE = 4,
  SUCCESS = 5,
  DEMO_REQUEST = 6,
}

type PipelineMode = "USE_TOC" | "USE_SNOWFLAKE" | "USE_SMART_AGENT";

interface FormErrors {
  email?: boolean;
  fullname?: boolean;
  companyName?: boolean;
  companyType?: boolean;
  terms?: boolean;
  generic?: string;
}

const GENERIC_INDUSTRY_KEY = "GENERIC";

const COMPANY_TYPES = [
  {
    value: OrganizationType.CLINICAL_SPECIALISTS,
    label: "Physicians / Assessors",
    description:
      "Medical assessments, specialist consultations, and clinical evaluations",
    tags: [
      "Assessor Reports",
      "Consultation Reports",
      "Clinical Notes",
      "Diagnostic Reports",
      "Medical Evaluations",
    ],
  },
  {
    value: OrganizationType.IME,
    label: "Independent Assessment (IME, IE)",
    description:
      "Independent medical examinations, insurance assessments, and claims evaluation",
    tags: [
      "IME Reports",
      "Functional Assessments",
      "Vocational Reports",
      "CAT Reports",
      "Treatment Plans",
    ],
  },
  {
    value: OrganizationType.UNDERWRITING,
    label: "Insurance",
    description:
      "Insurance policies, claims processing, risk assessments, and underwriting",
    tags: [
      "Policy Documents",
      "Claims Files",
      "Risk Assessments",
      "Underwriting Reports",
      "Coverage Analysis",
    ],
  },
  {
    value: GENERIC_INDUSTRY_KEY,
    label: "Other",
    description: "General document management for any industry",
    tags: ["Documents", "Reports", "Contracts", "Correspondence", "Records"],
  },
];

const VALID_INDUSTRIES: readonly OrganizationType[] = COMPANY_TYPES.map(
  (type) => type.value
).filter((v): v is OrganizationType => v !== GENERIC_INDUSTRY_KEY);

const isValidIndustry = (
  industry: string | null
): industry is OrganizationType => {
  return (
    industry !== null && VALID_INDUSTRIES.includes(industry as OrganizationType)
  );
};

export const RegisterForm = () => {
  const intl = useIntl();
  const { locale } = useContext(LocalizationContext);
  const [searchParams] = useSearchParams();
  const industryParam = searchParams.get("industry");
  const hasValidIndustryParam = isValidIndustry(industryParam);
  const skipDemo = searchParams.get("skip-demo") === "1";

  const [email, setEmail] = useState("");
  const [fullname, setFullname] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyType, setCompanyType] = useState<string | undefined>(
    hasValidIndustryParam ? industryParam : OrganizationType.IME
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [currentStep, setCurrentStep] = useState<REGISTER_STEP>(
    REGISTER_STEP.PERSONAL_DETAILS
  );
  const [termsAccepted, setTermsAccepted] = useState(false);
  const isFrench = locale === "fr";
  const [country, setCountry] = useState<"CA" | "US" | "OTHER">(() => {
    if (isFrench) {
      return "CA";
    }
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const US_TIMEZONE_PREFIXES = [
        "America/New_York",
        "America/Chicago",
        "America/Denver",
        "America/Los_Angeles",
        "America/Anchorage",
        "America/Phoenix",
        "America/Adak",
        "America/Boise",
        "America/Indiana",
        "America/Kentucky",
        "America/Menominee",
        "America/Metlakatla",
        "America/Nome",
        "America/North_Dakota",
        "America/Sitka",
        "America/Yakutat",
        "America/Juneau",
        "America/Detroit",
        "Pacific/Honolulu",
        "US/",
      ];
      if (US_TIMEZONE_PREFIXES.some((prefix) => tz.startsWith(prefix))) {
        return "US";
      }
      const CA_TIMEZONE_PREFIXES = [
        "America/Toronto",
        "America/Vancouver",
        "America/Edmonton",
        "America/Winnipeg",
        "America/Halifax",
        "America/St_Johns",
        "America/Regina",
        "America/Whitehorse",
        "America/Yellowknife",
        "America/Iqaluit",
        "America/Moncton",
        "America/Glace_Bay",
        "America/Goose_Bay",
        "America/Rankin_Inlet",
        "America/Resolute",
        "America/Swift_Current",
        "America/Dawson",
        "America/Dawson_Creek",
        "America/Fort_Nelson",
        "America/Creston",
        "America/Cambridge_Bay",
        "America/Inuvik",
        "America/Atikokan",
        "America/Nipigon",
        "America/Thunder_Bay",
        "America/Pangnirtung",
        "Canada/",
      ];
      if (CA_TIMEZONE_PREFIXES.some((prefix) => tz.startsWith(prefix))) {
        return "CA";
      }
    } catch {
      // Default to OTHER if timezone detection fails
    }
    return "OTHER";
  });
  const { mutate, isPending } = useCreateOrganization();
  const { trackCreateAccount } = useTrackAccountCreation();
  const queryClient = useQueryClient();

  const handleSocialLogin = (provider: SocialProvider) => {
    trackEvent({
      eventName: `app/register/social_login.${provider.toLowerCase()}/click`,
    });
    initiateOAuthLogin(provider);
  };

  useEffect(() => {
    if (searchParams.get("guest") === "true") {
      removeAllTokens();
      queryClient.setQueryData(getGetCurrentUserQueryKey(), null);
    }
  }, [searchParams, queryClient]);

  const createAccountBg = useThemedOnboardingImage("create-account");
  const companyNameBg = useThemedOnboardingImage("company-name");
  const locationBg = useThemedOnboardingImage("location");
  const successBg = useThemedOnboardingImage("success");

  const stepImage = useMemo(() => {
    switch (currentStep) {
      case REGISTER_STEP.PERSONAL_DETAILS:
        return createAccountBg;
      case REGISTER_STEP.COMPANY_DETAILS_NAME:
      case REGISTER_STEP.COMPANY_DETAILS_TYPE:
        return companyNameBg;
      case REGISTER_STEP.LOCATION:
        return locationBg;
      case REGISTER_STEP.SUCCESS:
        return successBg;
      case REGISTER_STEP.DEMO_REQUEST:
        return undefined;
      default:
        return createAccountBg;
    }
  }, [currentStep, createAccountBg, companyNameBg, locationBg, successBg]);

  useAuthStepImage(stepImage);

  const { displayStep, totalSteps } = useMemo(() => {
    if (hasValidIndustryParam) {
      return { displayStep: currentStep, totalSteps: 2 };
    }
    if (isFrench) {
      const stepMap: Record<number, number> = {
        [REGISTER_STEP.PERSONAL_DETAILS]: 1,
        [REGISTER_STEP.COMPANY_DETAILS_NAME]: 2,
        [REGISTER_STEP.COMPANY_DETAILS_TYPE]: 3,
      };
      return {
        displayStep: stepMap[currentStep] ?? currentStep,
        totalSteps: 3,
      };
    }
    return { displayStep: currentStep, totalSteps: 4 };
  }, [currentStep, isFrench, hasValidIndustryParam]);

  const validateFirstStep = () => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!validateEmail(email)) {
      newErrors.email = true;
      isValid = false;
    } else if (!skipDemo && !isCustomEmailDomain(email)) {
      // Temporary: Show inline Calendly for public/free email domains to test lead quality
      setCurrentStep(REGISTER_STEP.DEMO_REQUEST);
      return false;
    }

    if (!fullname.trim()) {
      newErrors.fullname = true;
      isValid = false;
    }

    if (!termsAccepted) {
      newErrors.terms = true;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateSecondStep = () => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!companyName.trim()) {
      newErrors.companyName = true;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateSecondDetailsStep = () => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!companyType) {
      newErrors.companyType = true;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const submitRegistration = () => {
    const pipelineMode: PipelineMode = "USE_SMART_AGENT";
    const language = locale === "fr" ? "fr" : "en";
    trackEvent({
      eventName: "app/register/register.button/submit",
      properties: {
        email,
        fullname,
        companyName,
        companyType,
        pipelineMode,
        language,
      },
    });
    mutate(
      {
        data: {
          ownerEmail: email,
          ownerName: fullname,
          orgName: companyName,
          orgType:
            companyType === GENERIC_INDUSTRY_KEY
              ? OrganizationType.IME
              : (companyType ?? OrganizationType.IME),
          pipelineMode,
          language,
          country: country === "OTHER" ? "US" : country,
        } as Parameters<typeof mutate>[0]["data"] & {
          pipelineMode: PipelineMode;
          language: "en" | "fr";
          country: "CA" | "US";
        },
      },
      {
        onSuccess: async () => {
          trackCreateAccount();
          localStorage.setItem(SKY_ACCOUNT_CREATED, "true");
          removeAllTokens();
          queryClient.setQueryData(getGetCurrentUserQueryKey(), null);
          await queryClient.invalidateQueries();
          setCurrentStep(REGISTER_STEP.SUCCESS);
        },
        onError: (error: unknown) => {
          const axiosError = error as {
            response?: {
              status?: number;
              data?: { code?: number; message?: string };
            };
          };
          const httpStatus = axiosError?.response?.status;
          const bodyCode = axiosError?.response?.data?.code;
          const isUserAlreadyExists = httpStatus === 409 || bodyCode === 409;

          if (isUserAlreadyExists) {
            setErrors((prev) => ({ ...prev, duplicateEmail: true }));
            setCurrentStep(REGISTER_STEP.PERSONAL_DETAILS);
          } else {
            logger.error("Registration error:", error);
            const errorMessage =
              axiosError?.response?.data?.message ||
              "An error occurred while creating your account. Please try again.";
            setErrors((prev) => ({ ...prev, generic: errorMessage }));
          }
        },
      }
    );
  };

  const handleContinue = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    removeAllTokens();
    queryClient.setQueryData(getGetCurrentUserQueryKey(), null);
    queryClient.invalidateQueries();

    if (currentStep === REGISTER_STEP.PERSONAL_DETAILS) {
      if (validateFirstStep()) {
        setCurrentStep(REGISTER_STEP.COMPANY_DETAILS_NAME);
      }
    } else if (currentStep === REGISTER_STEP.COMPANY_DETAILS_NAME) {
      if (validateSecondStep()) {
        if (hasValidIndustryParam) {
          submitRegistration();
        } else if (isFrench) {
          setCountry("CA");
          setCurrentStep(REGISTER_STEP.COMPANY_DETAILS_TYPE);
        } else {
          setCurrentStep(REGISTER_STEP.LOCATION);
        }
      }
    } else if (currentStep === REGISTER_STEP.LOCATION) {
      setCurrentStep(REGISTER_STEP.COMPANY_DETAILS_TYPE);
    } else if (currentStep === REGISTER_STEP.COMPANY_DETAILS_TYPE) {
      if (validateSecondDetailsStep()) {
        submitRegistration();
      }
    }
  };

  return (
    <div className="flex justify-center items-start">
      <div
        className={`flex flex-col gap-4 sm:gap-6 w-full ${
          currentStep === REGISTER_STEP.DEMO_REQUEST
            ? "max-w-4xl"
            : "max-w-[560px]"
        }`}
      >
        <div className="flex items-center justify-between">
          {currentStep === REGISTER_STEP.PERSONAL_DETAILS ? (
            <UsersRound className="h-[32px] w-[32px] text-[#71717A]" />
          ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_NAME ||
            currentStep === REGISTER_STEP.COMPANY_DETAILS_TYPE ? (
            <Factory className="h-[32px] w-[32px] text-[#71717A]" />
          ) : currentStep === REGISTER_STEP.LOCATION ? (
            <Globe className="h-[32px] w-[32px] text-[#71717A]" />
          ) : currentStep === REGISTER_STEP.SUCCESS ? (
            <Mail className="h-[32px] w-[32px] text-[#71717A]" />
          ) : (
            <UsersRound className="h-[32px] w-[32px] text-[#71717A]" />
          )}
          {currentStep >= REGISTER_STEP.PERSONAL_DETAILS &&
            currentStep <= REGISTER_STEP.COMPANY_DETAILS_TYPE && (
              <StepIndicator
                currentStep={displayStep}
                totalSteps={totalSteps}
              />
            )}
        </div>
        <div>
          <h3 className="text-2xl font-semibold text-base-foreground pt-2 sm:pt-[16px]">
            {currentStep === REGISTER_STEP.PERSONAL_DETAILS ? (
              <FormattedMessage
                id="auth.register.title"
                defaultMessage="Create Account"
              />
            ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_NAME ? (
              <FormattedMessage
                id="auth.register.companyDetails"
                defaultMessage="Company"
              />
            ) : currentStep === REGISTER_STEP.LOCATION ? (
              <FormattedMessage
                id="auth.register.location"
                defaultMessage="Location"
              />
            ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_TYPE ? (
              <FormattedMessage
                id="auth.register.companyDetailsType"
                defaultMessage="Industry"
              />
            ) : currentStep === REGISTER_STEP.SUCCESS ? (
              <FormattedMessage
                id="auth.register.checkEmail"
                defaultMessage="Check Your Email"
              />
            ) : (
              <FormattedMessage
                id="auth.register.demoRequest"
                defaultMessage="Request a Demo"
              />
            )}
          </h3>
          <p className="text-sm text-[#71717A] mt-[16px]">
            {currentStep === REGISTER_STEP.PERSONAL_DETAILS ? (
              <FormattedMessage
                id="auth.register.subtitle"
                defaultMessage="Please enter your email address and full name to create a Sky AI account."
              />
            ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_NAME ? (
              <FormattedMessage
                id="auth.register.companyDetailsSubtitle"
                defaultMessage="Please provide your company name."
              />
            ) : currentStep === REGISTER_STEP.LOCATION ? (
              <FormattedMessage
                id="auth.register.locationSubtitle"
                defaultMessage="Where is your organization based?"
              />
            ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_TYPE ? (
              <FormattedMessage
                id="auth.register.companyDetailsTypeSubtitle"
                defaultMessage="What kinds of documents do you usually deal with at work?"
              />
            ) : currentStep === REGISTER_STEP.SUCCESS ? (
              <FormattedMessage
                id="auth.register.verificationEmail"
                defaultMessage="We've emailed you a temporary password and registration link. If not received within an hour, check spam or contact support."
                values={{
                  support: (chunks) => (
                    <a
                      href="mailto:support@usesky.ai"
                      className="text-blue-600 hover:underline"
                    >
                      {chunks}
                    </a>
                  ),
                }}
              />
            ) : (
              <FormattedMessage
                id="auth.register.demoRequest"
                defaultMessage="Request a Demo"
              />
            )}
          </p>
        </div>
        <form
          onSubmit={handleContinue}
          className="w-full flex flex-col gap-[24px] text-sm"
        >
          {currentStep === REGISTER_STEP.PERSONAL_DETAILS ? (
            <>
              <div className="w-full flex flex-col gap-[16px]">
                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="registerEmail"
                    className="block font-medium text-base-foreground"
                  >
                    <FormattedMessage
                      id="auth.register.email"
                      defaultMessage="Email"
                    />
                  </Label>
                  <Input
                    id="registerEmail"
                    placeholder={intl.formatMessage({
                      id: "auth.register.emailPlaceholder",
                      defaultMessage: "Enter your email",
                    })}
                    value={email}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-none ${
                      errors.email ? "border-red-600" : "border-gray-300"
                    }`}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => {
                      if (email.trim()) {
                        setErrors((prev) => ({
                          ...prev,
                          email: !validateEmail(email),
                        }));
                      }
                    }}
                  />
                  {errors.email && (
                    <span className="text-red-600">
                      <FormattedMessage
                        id="auth.register.emailError"
                        defaultMessage="Email should be <strong>example@site.com</strong>"
                        values={{
                          strong: (chunks) => (
                            <strong className="font-semibold">{chunks}</strong>
                          ),
                        }}
                      />
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="registerFullname"
                    className="block font-medium text-base-foreground"
                  >
                    <FormattedMessage
                      id="auth.register.fullname"
                      defaultMessage="Full name"
                    />
                  </Label>
                  <Input
                    id="registerFullname"
                    type="text"
                    placeholder={intl.formatMessage({
                      id: "auth.register.fullnamePlaceholder",
                      defaultMessage: "Enter your full name",
                    })}
                    value={fullname}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-none ${
                      errors.fullname ? "border-red-600" : "border-gray-300"
                    }`}
                    onChange={(e) => {
                      setFullname(e.target.value);
                    }}
                  />
                  {errors.fullname && (
                    <span className="text-sm text-red-600">
                      <FormattedMessage
                        id="auth.register.fullnameError"
                        defaultMessage="This field cannot be empty"
                      />
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center space-x-3 py-2">
                    <Checkbox
                      id="terms"
                      checked={termsAccepted}
                      onCheckedChange={(checked) => {
                        setTermsAccepted(checked as boolean);
                      }}
                      className={`cursor-pointer h-5 w-5 sm:h-4 sm:w-4 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 border border-[#E4E4E7] shadow-none ${errors.terms ? "border-red-600" : ""}`}
                    />
                    <div className="flex flex-col gap-1">
                      <Label
                        htmlFor="terms"
                        className="font-[400] text-base-foreground cursor-pointer"
                      >
                        <FormattedMessage
                          id="auth.register.termsAgreement"
                          defaultMessage="Agree to the {terms} and {privacy}"
                          values={{
                            terms: (
                              <TermsAndPolicyDialog type="terms">
                                <span className="text-blue-600 cursor-pointer">
                                  <FormattedMessage
                                    id="auth.register.terms"
                                    defaultMessage="Terms and Conditions"
                                  />
                                </span>
                              </TermsAndPolicyDialog>
                            ),
                            privacy: (
                              <TermsAndPolicyDialog type="policy">
                                <span className="text-blue-600 cursor-pointer">
                                  <FormattedMessage
                                    id="auth.register.privacy"
                                    defaultMessage="Privacy Policy"
                                  />
                                </span>
                              </TermsAndPolicyDialog>
                            ),
                          }}
                        />
                      </Label>
                    </div>
                  </div>
                  {errors.terms && (
                    <span className="text-red-600">
                      <FormattedMessage
                        id="auth.register.termsError"
                        defaultMessage="You must agree to the Terms and Conditions and Privacy Policy"
                      />
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_NAME ? (
            <>
              <div className="flex flex-col gap-2">
                <Label
                  htmlFor="companyName"
                  className="block font-medium text-base-foreground"
                >
                  <FormattedMessage
                    id="auth.register.companyName"
                    defaultMessage="Company name"
                  />
                </Label>
                <Input
                  id="companyName"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: "auth.register.companyNamePlaceholder",
                    defaultMessage: "Enter your company name",
                  })}
                  value={companyName}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.companyName ? "border-red-600" : "border-gray-300"
                  }`}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                  }}
                />
                {errors.companyName && (
                  <span className="text-sm text-red-600">
                    <FormattedMessage
                      id="auth.register.companyNameError"
                      defaultMessage="Company name cannot be empty"
                    />
                  </span>
                )}
                {errors.generic && (
                  <span className="text-sm text-red-600">{errors.generic}</span>
                )}
              </div>
            </>
          ) : currentStep === REGISTER_STEP.LOCATION ? (
            <LocationSelector country={country} onCountryChange={setCountry} />
          ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_TYPE ? (
            <>
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto p-1">
                  {COMPANY_TYPES.map((type) => (
                    <div
                      key={type.value}
                      role="button"
                      tabIndex={0}
                      aria-pressed={companyType === type.value}
                      aria-label={intl.formatMessage({
                        id: `auth.register.companyType.${type.value}`,
                        defaultMessage: type.label,
                      })}
                      onClick={() => setCompanyType(type.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setCompanyType(type.value);
                        }
                      }}
                      className={`
                        relative flex cursor-pointer rounded-lg border p-3 sm:p-4 transition-all focus:ring-2 focus:ring-[#60A5FA] focus:outline-none
                        ${
                          companyType === type.value
                            ? "border-[#60A5FA] ring-1 ring-[#60A5FA] bg-sky-50 dark:bg-sky-950"
                            : "border-border hover:border-border/80 hover:bg-accent bg-background"
                        }
                      `}
                    >
                      <div className="flex flex-col sm:flex-row flex-1 gap-2 sm:gap-4">
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="block text-sm font-[500] text-foreground">
                            <FormattedMessage
                              id={`auth.register.companyType.${type.value}`}
                              defaultMessage={type.label}
                            />
                          </span>
                          <span className="mt-1 sm:mt-[6px] text-xs text-muted-foreground">
                            <FormattedMessage
                              id={`auth.register.companyType.${type.value}.description`}
                              defaultMessage={type.description}
                            />
                          </span>
                        </div>
                        <div className="hidden sm:flex flex-wrap gap-y-[6px] gap-x-[4px] justify-start items-start max-w-[256px]">
                          {type.tags.slice(0, 5).map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center rounded-full bg-muted px-2 py-[2px] text-xs text-foreground"
                            >
                              <FormattedMessage
                                id={`auth.register.companyType.${type.value}.tag.${index}`}
                                defaultMessage={tag}
                              />
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {errors.companyType && (
                  <span className="text-sm text-red-600">
                    <FormattedMessage
                      id="auth.register.companyTypeError"
                      defaultMessage="Please select the type of company"
                    />
                  </span>
                )}
                {errors.generic && (
                  <span className="text-sm text-red-600">{errors.generic}</span>
                )}
              </div>
            </>
          ) : currentStep === REGISTER_STEP.SUCCESS ? (
            <div className="flex flex-col gap-4">
              <Button asChild>
                <Link to={ROUTES.LOGIN}>
                  <FormattedMessage
                    id="auth.register.backToLogin"
                    defaultMessage="Log In"
                  />
                </Link>
              </Button>
            </div>
          ) : (
            <div className="w-full max-w-[1400px] mx-auto px-4 flex justify-center">
              <RegisterDemoWidget
                email={email}
                name={fullname.trim()}
                onBack={() => setCurrentStep(REGISTER_STEP.PERSONAL_DETAILS)}
              />
            </div>
          )}
          {currentStep === REGISTER_STEP.PERSONAL_DETAILS ? (
            <div className="flex flex-col gap-[16px]">
              <Button>
                <FormattedMessage
                  id="auth.register.continue"
                  defaultMessage="Continue"
                />
              </Button>
              <span className="text-sm text-muted-foreground font-semibold text-center">
                <FormattedMessage id="auth.register.or" defaultMessage="or" />
              </span>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => handleSocialLogin("Google")}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                <FormattedMessage
                  id="auth.register.continueWithGoogle"
                  defaultMessage="Continue with Google"
                />
              </Button>
              <Button variant="secondary" asChild>
                <Link to={ROUTES.LOGIN}>
                  <FormattedMessage
                    id="auth.register.login"
                    defaultMessage="Log In"
                  />
                </Link>
              </Button>
            </div>
          ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_NAME ? (
            <>
              <Button disabled={hasValidIndustryParam && isPending}>
                {hasValidIndustryParam && isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                <FormattedMessage
                  id={
                    hasValidIndustryParam && isPending
                      ? "auth.register.creatingAccount"
                      : "auth.register.continue"
                  }
                  defaultMessage={
                    hasValidIndustryParam && isPending
                      ? "Creating Account..."
                      : "Continue"
                  }
                />
              </Button>
            </>
          ) : currentStep === REGISTER_STEP.LOCATION ? (
            <Button>
              <FormattedMessage
                id="auth.register.continue"
                defaultMessage="Continue"
              />
            </Button>
          ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_TYPE ? (
            <>
              <Button disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <FormattedMessage
                  id={
                    isPending
                      ? "auth.register.creatingAccount"
                      : "auth.register.continue"
                  }
                  defaultMessage={
                    isPending ? "Creating Account..." : "Continue"
                  }
                />
              </Button>
            </>
          ) : null}
        </form>
      </div>
    </div>
  );
};
